import { GeoCoordinates } from "@type/geolocation.types"
import { AdvancedMarker } from "@vis.gl/react-google-maps"
import cx from "classnames"
import { useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import Icon from "./Icon"
import { ShiftStatus } from "config/constants"
import { Image } from "antd-v5"

interface CustomAdvancedMarkerProps {
	loading: boolean
	pinColor: string
	position: GeoCoordinates
	markerEventDetails: {
		name?: string
		eventTime: string
		dueTime: string
		status: ShiftStatus
		employee: string
		images: string[]
	}
	onMarkerClick: (markerPosition: { lat: number; lng: number }, clickState: boolean) => void
	isMarkerSelected: () => boolean
}

const CustomAdvancedMarker: React.FC<CustomAdvancedMarkerProps> = ({
	loading,
	position,
	pinColor,
	markerEventDetails: { images, dueTime, employee, eventTime, name = "", status },
	onMarkerClick,
	isMarkerSelected,
}) => {
	const [clicked, setClicked] = useState(false)
	const [hovered, setHovered] = useState(false)

	const isMarkerHovered = useMemo(() => {
		return isMarkerSelected() || hovered
	}, [hovered, isMarkerSelected])

	const isMarkerClicked = useMemo(() => {
		return isMarkerSelected() && clicked
	}, [clicked, isMarkerSelected])

	const renderCustomPin = () => {
		return (
			<>
				<div
					id="custom-pin"
					style={{
						background: pinColor,
					}}
					className={twMerge(
						cx(
							"relative flex h-[34px] w-fit max-w-[34px] origin-bottom items-center justify-center rounded-[50%] p-1 transition-[max-width,height] duration-150 ease-in-out",
							// "relative flex h-[34px] w-fit max-w-[34px] origin-bottom items-center justify-center rounded-[50%] bg-[#12271e] p-1 transition-[max-width,height] duration-150 ease-in-out",
							{
								"h-20 max-w-20 rounded-[50%]": isMarkerHovered,
								"h-[250px] w-fit max-w-[650px] rounded-none bg-white shadow-md": isMarkerClicked,
							},
						),
					)}
				>
					<div
						id="image-container"
						className={twMerge(
							cx(
								"relative flex h-full w-[200px] items-center justify-center overflow-hidden rounded-[inherit] bg-cover bg-center transition-opacity duration-200 ease-in-out",
								{ "opacity-1 border-inherit": isMarkerHovered, "border-inherit": isMarkerClicked },
							),
						)}
					>
						<MarkerGallery images={images} isExtended={isMarkerClicked} isHovered={isMarkerHovered} />

						<div
							id="icon"
							className={twMerge(
								cx("absolute opacity-100 transition-opacity duration-200 ease-in-out", {
									"border-inherit opacity-0": isMarkerHovered,
									"invisible opacity-0": isMarkerClicked,
								}),
							)}
						>
							{loading ? (
								<Icon name="spinner" color="black" className="animate-spin" />
							) : (
								<img src={images[0]} className="h-8 w-8 object-cover" />
							)}
						</div>
					</div>

					<MarkerEventDetails
						clicked={isMarkerClicked}
						rootClassName="py-4 px-4 space-y-4 text-[14px] text-gray-900"
					>
						<div className="flex items-center justify-between py-1">
							<div className="text-3.5 font-semibold">Event Details</div>
							<Icon name="cross" color="black" size={20} />
						</div>
						<div className="flex space-x-10">
							<span className="min-w-20 text-gray-500 ">Event: </span>
							<span>{status}</span>
						</div>
						{name && (
							<div className="flex space-x-10">
								<span className="min-w-20 text-gray-500">QR Name:</span>
								<span>{name}</span>
							</div>
						)}
						<div className="flex space-x-10">
							<span className="min-w-20 text-gray-500">Due Time:</span>
							<span>{dueTime}</span>
						</div>

						<div className="flex space-x-10">
							<span className="min-w-20 text-gray-500">Event Time:</span>
							<span>{eventTime}</span>
						</div>

						<div className="flex space-x-10">
							<span className="min-w-20 text-gray-500 ">Officer: </span>
							<span>{employee}</span>
						</div>

						<div className="flex space-x-10 ">
							<span className="min-w-20 text-gray-500 ">Location: </span>
							<span>{`${position.lat.toFixed(5)} , ${position.lng.toFixed(5)}`}</span>
						</div>
					</MarkerEventDetails>
				</div>

				<div
					id="tip"
					style={{
						borderColor: pinColor,
					}}
					className={twMerge(
						cx(
							"absolute bottom-0 left-1/2 -z-10 h-0 w-0 translate-y-[22%] -translate-x-1/2 rotate-45 transform rounded-none rounded-br-[5px] border-2 border-[#12271e] transition-all duration-200 ease-in-out",
							{
								"scale-140 translate-y-[23%] -translate-x-1/2 rotate-45 transform":
									isMarkerHovered || isMarkerClicked,
							},
						),
					)}
				/>
			</>
		)
	}

	return (
		<div
			className={twMerge(
				cx("relative", {
					"z-50": isMarkerClicked,
					"z-20": isMarkerHovered,
				}),
			)}
		>
			<AdvancedMarker
				zIndex={isMarkerClicked ? 999 : isMarkerHovered ? 1000 : 0}
				position={position}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				className={twMerge(
					cx("relative -translate-y-[5px] transform cursor-pointer transition-all duration-200 ease-in-out"),
				)}
				onClick={e => {
					setClicked(!clicked)
					onMarkerClick?.({ lat: e.latLng?.lat() ?? 0, lng: e.latLng?.lng() ?? 0 }, clicked)
				}}
			>
				{renderCustomPin()}
			</AdvancedMarker>
		</div>
	)
}

const MarkerGallery: React.FC<{ images: string[]; isExtended: boolean; isHovered: boolean }> = ({
	images = [],
	isExtended = false,
	isHovered = false,
}) => {
	// const [currentImageIndex, setCurrentImageIndex] = useState(0)
	const [previewImage, setPreviewImage] = useState(false)

	// const handleBack = (event: MouseEvent<HTMLDivElement>) => {
	// 	event.stopPropagation()
	// 	if (currentImageIndex > 0) {
	// 		setCurrentImageIndex(currentImageIndex - 1)
	// 	}
	// }

	// const handleNext = (event: MouseEvent<HTMLDivElement>) => {
	// 	event.stopPropagation()
	// 	if (currentImageIndex < images.length - 1) {
	// 		setCurrentImageIndex(currentImageIndex + 1)
	// 	}
	// }

	return (
		<div className="h-full w-full">
			{isExtended && isHovered ? (
				<Image.PreviewGroup
					preview={{
						visible: previewImage,
						onVisibleChange: vis => setPreviewImage(vis),
					}}
				>
					{images.map(src => (
						<Image
							key={src}
							onClick={event => {
								event.stopPropagation()
								setPreviewImage(true)
							}}
							src={src}
							alt="avatar"
						/>
					))}
				</Image.PreviewGroup>
			) : (
				images.length && (
					<img src={images[0]} className={twMerge(cx("h-full w-full", { "object-cover": isHovered }))} />
				)
			)}

			{/* Below code is the alternate to Image.PreviewGroup */}
			{/* <div
				className={twMerge(
					cx("invisible absolute inset-0 flex flex-col items-center justify-end p-4 px-2 opacity-0", {
						"visible opacity-100 transition-opacity duration-300 ease-in": isExtended,
					}),
				)}
			>
				<div className="my-auto flex w-full translate-y-1/2 justify-between">
					<div onClick={handleBack} className={`${currentImageIndex === 0 ? "cursor-not-allowed" : ""}`}>
						<Icon name="leftArrow" />
					</div>

					<div
						onClick={handleNext}
						className={`${currentImageIndex === images.length - 1 ? "cursor-not-allowed" : ""}`}
					>
						<Icon name="rightArrow" />
					</div>
				</div>
				<div>
					{images.map((_, index) => (
						<span
							key={index}
							id="dot"
							className={twMerge(
								cx(
									`ease mx-[2px] inline-block h-[6px] w-[6px] cursor-pointer rounded-full bg-[rgba(255,255,255,0.6)] transition-colors duration-300`,
									{ "bg-white": index === currentImageIndex },
								),
							)}
						/>
					))}
				</div>
			</div> */}
		</div>
	)
}

interface MarkerEventDetailProps {
	clicked: boolean
	children: React.ReactNode
	rootClassName?: string
}

const MarkerEventDetails: React.FC<MarkerEventDetailProps> = ({ clicked, children, rootClassName = "" }) => {
	return (
		<div
			id="details-container"
			className={twMerge(
				cx("invisible h-full max-w-0 rounded-tr-[inherit] rounded-br-[inherit] bg-white opacity-0", {
					"visible max-w-[300px] animate-slideInFadeIn opacity-100": clicked,
				}),
			)}
		>
			<div id="listing-content" className={twMerge("h-full overflow-hidden", rootClassName)}>
				{children}
			</div>
		</div>
	)
}

export default CustomAdvancedMarker
